/**
 * UserPreferencesService – manages user preferences stored in the User Settings API.
 *
 * Currently handles:
 * - feedback_widget_disabled: persistent preference to hide the feedback FAB
 *
 * Re-uses the same User Settings record as the CustomerService (one record per user).
 * Emits 'user-preferences-changed' event when preferences change.
 */

import { ApiClient } from './api-client';
import { eventBus } from '../../infrastructure/event-bus';
import type { UserSettingsRecord } from '../models/customer-entry.model';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserPreferences {
  feedback_widget_disabled: boolean;
  /** Which assortment sources to show in quick-entry: 'Meine', 'Kunde', 'Global'. Empty = all. */
  quick_entry_sources: string[];
  /** Whether to show the hardware scanner in navigation. */
  hardware_scanner_view: boolean;
}

// ---------------------------------------------------------------------------
// API Client
// ---------------------------------------------------------------------------

function getApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_BASE_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

const settingsApiClient = new ApiClient(getApiBaseUrl());

// ---------------------------------------------------------------------------
// UserPreferencesService class
// ---------------------------------------------------------------------------

const ALL_SOURCES = ['Meine', 'Kunde', 'Global'];

export class UserPreferencesService {
  private preferences: UserPreferences = { feedback_widget_disabled: false, quick_entry_sources: ALL_SOURCES, hardware_scanner_view: false };
  private recordId: string | null = null;
  private loaded = false;
  private loading = false;

  // -- Query ----------------------------------------------------------------

  getPreferences(): UserPreferences {
    return { ...this.preferences };
  }

  isFeedbackWidgetDisabled(): boolean {
    return this.preferences.feedback_widget_disabled;
  }

  getQuickEntrySources(): string[] {
    return this.preferences.quick_entry_sources.length > 0
      ? [...this.preferences.quick_entry_sources]
      : ALL_SOURCES;
  }

  isHardwareScannerViewEnabled(): boolean {
    return this.preferences.hardware_scanner_view;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  // -- Load -----------------------------------------------------------------

  /**
   * Load preferences from the User Settings API.
   * Reads the first record for the authenticated user.
   */
  async loadPreferences(): Promise<UserPreferences> {
    if (this.loading) return this.getPreferences();
    this.loading = true;

    try {
      const records = await settingsApiClient.get<UserSettingsRecord[]>(
        '/api/user-settings?perPage=1',
      );

      if (records.length > 0) {
        this.recordId = records[0].id;
        this.preferences = {
          feedback_widget_disabled: records[0].feedback_widget_disabled ?? false,
          quick_entry_sources: records[0].quick_entry_sources ?? ALL_SOURCES,
          hardware_scanner_view: records[0].hardware_scanner_view === true,
        };
      } else {
        this.recordId = null;
        this.preferences = { feedback_widget_disabled: false, quick_entry_sources: ALL_SOURCES, hardware_scanner_view: false };
      }

      this.loaded = true;
      this.notifyChange();
      return this.getPreferences();
    } finally {
      this.loading = false;
    }
  }

  // -- Mutations ------------------------------------------------------------

  /**
   * Update the feedback_widget_disabled preference.
   * Persists to the API immediately.
   */
  async setFeedbackWidgetDisabled(disabled: boolean): Promise<void> {
    const body = { feedback_widget_disabled: disabled };

    if (this.recordId) {
      await settingsApiClient.put<UserSettingsRecord>(
        `/api/user-settings/${encodeURIComponent(this.recordId)}`,
        body,
      );
    } else {
      const response = await settingsApiClient.post<UserSettingsRecord>(
        '/api/user-settings',
        body,
      );
      this.recordId = response.id;
    }

    this.preferences.feedback_widget_disabled = disabled;
    this.notifyChange();
  }

  /**
   * Update the quick-entry source filter.
   * Persists to the API immediately.
   * @param sources Array of enabled sources: 'Meine', 'Kunde', 'Global'
   */
  async setQuickEntrySources(sources: string[]): Promise<void> {
    // Ensure at least one source is enabled
    const validated = sources.length > 0 ? sources : ALL_SOURCES;
    const body = { quick_entry_sources: validated };

    if (this.recordId) {
      await settingsApiClient.put<UserSettingsRecord>(
        `/api/user-settings/${encodeURIComponent(this.recordId)}`,
        body,
      );
    } else {
      const response = await settingsApiClient.post<UserSettingsRecord>(
        '/api/user-settings',
        body,
      );
      this.recordId = response.id;
    }

    this.preferences.quick_entry_sources = validated;
    this.notifyChange();
  }

  /**
   * Update the hardware_scanner_view preference.
   * Persists to the API immediately.
   */
  async setHardwareScannerView(enabled: boolean): Promise<void> {
    const body = { hardware_scanner_view: enabled };

    if (this.recordId) {
      await settingsApiClient.put<UserSettingsRecord>(
        `/api/user-settings/${encodeURIComponent(this.recordId)}`,
        body,
      );
    } else {
      const response = await settingsApiClient.post<UserSettingsRecord>(
        '/api/user-settings',
        body,
      );
      this.recordId = response.id;
    }

    this.preferences.hardware_scanner_view = enabled;
    this.notifyChange();
  }

  // -- Reset ----------------------------------------------------------------

  reset(): void {
    this.preferences = { feedback_widget_disabled: false, quick_entry_sources: ALL_SOURCES, hardware_scanner_view: false };
    this.recordId = null;
    this.loaded = false;
    this.notifyChange();
  }

  // -- Events ---------------------------------------------------------------

  private notifyChange(): void {
    eventBus.emit('user-preferences-changed', { preferences: this.preferences });
  }

  onPreferencesChanged(callback: (prefs: UserPreferences) => void): () => void {
    return eventBus.on<{ preferences: UserPreferences }>('user-preferences-changed', (detail) => {
      callback(detail.preferences);
    });
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const userPreferencesService = new UserPreferencesService();
